import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Sparkles, Check, X, ArrowLeft, Loader2, Star, Zap, Crown, 
  HelpCircle, Tag, Users, Building
} from 'lucide-react';
import PromoCodeInput from '@/components/PromoCodeInput';
import AnnouncementBanner from '@/components/AnnouncementBanner';
import SEO from '@/components/SEO';
import { ShieldCheck } from 'lucide-react';

// Build version: 2026-02-26-v3 - Added promo code support

const PricingPage = () => {
  const { isAuthenticated, user, api } = useAuth();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [searchParams] = useSearchParams();

  // Auto-apply promo code from shareable URL: /pricing?code=FAMILY3&plan=family
  // Also accepts &welcome=1&promo=FAMILY3 from the org signup redirect.
  useEffect(() => {
    const code = (searchParams.get('code') || searchParams.get('promo') || '').toUpperCase().trim();
    const planParam = (searchParams.get('plan') || '').toLowerCase().trim();
    if (planParam && ['free', 'basic', 'family', 'premium'].includes(planParam)) {
      setSelectedPlan(planParam);
    }
    if (!code || appliedPromo) return;
    const targetPlan = planParam || 'basic';
    (async () => {
      try {
        const response = await api.post(`/promo-codes/validate?code=${code}&plan=${targetPlan}`);
        if (response.data?.valid) {
          const d = response.data.discount;
          setAppliedPromo({
            code: d.code,
            discount_type: d.discount_type,
            discount_value: d.discount_value,
            description: d.description,
            stripe_coupon_id: d.stripe_coupon_id,
          });
          toast.success(`Promo code "${d.code}" applied from your link!`);
        }
      } catch (err) {
        const message = err?.response?.data?.detail || `Promo code "${code}" is not valid`;
        toast.error(message);
      }
    })();
    // Run once per URL change; api ref is stable per session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      period: 'forever',
      description: 'Try Zazu with basic features',
      icon: Star,
      features: [
        '1 child profile',
        '5 activities per day',
        'Basic subjects (ABC, 123, Shapes)',
        'Basic progress tracking',
      ],
      notIncluded: [
        'AI-powered learning',
        'Voice input & text-to-speech',
        'Adaptive difficulty',
        'All subjects & languages',
        'PDF reports & analytics',
        'Weekly progress reports',
        'Ad-free experience',
      ],
      buttonText: 'Get Started Free',
      buttonVariant: 'outline'
    },
    {
      id: 'parent_basic',
      name: 'Basic',
      price: 4.99,
      period: 'month',
      description: 'Great for individuals',
      icon: Zap,
      features: [
        '1 child profile',
        'Unlimited activities',
        'All core subjects',
        'AI-powered adaptive learning',
        'Voice input (speech-to-text)',
        'Text-to-speech for reading',
        'Basic progress analytics',
        'Ad-free experience',
      ],
      notIncluded: [
        'Multiple children',
        'Weekly email reports',
        'Bible curriculum',
        'Priority support',
      ],
      buttonText: 'Subscribe',
      buttonVariant: 'default'
    },
    {
      id: 'parent_family',
      name: 'Family',
      price: 6.99,
      period: 'month',
      description: 'Perfect for growing families',
      icon: Users,
      popular: true,
      features: [
        'Up to 5 child profiles',
        'Unlimited activities',
        'All core subjects',
        'AI-powered adaptive learning',
        'Voice input (speech-to-text)',
        'Text-to-speech for reading',
        'Detailed progress analytics',
        'Weekly email reports',
        'PDF progress reports',
        'Ad-free experience',
        'Offline access',
      ],
      notIncluded: [
        'Unlimited children',
        'Bible curriculum',
        'Priority support',
      ],
      buttonText: 'Subscribe',
      buttonVariant: 'default'
    },
    {
      id: 'parent_premium',
      name: 'Premium',
      price: 9.99,
      period: 'month',
      description: 'Complete learning experience',
      icon: Crown,
      features: [
        'Unlimited child profiles',
        'Everything in Family, plus:',
        'Advanced AI curriculum',
        'Personalized learning paths',
        'Bible curriculum & stories',
        'Multiplayer learning games',
        'Family mode & parental controls',
        'Custom curriculum options',
        'Export all data & reports',
        'Priority customer support',
        'Early access to new features',
      ],
      notIncluded: [],
      buttonText: 'Subscribe',
      buttonVariant: 'default'
    }
  ];

  const handleSubscribe = async (planId) => {
    if (!isAuthenticated) {
      toast.info('Please sign in to subscribe');
      navigate('/login', { state: { from: '/pricing' } });
      return;
    }

    if (planId === 'free') {
      toast.info('You already have free access!');
      return;
    }

    setLoadingPlan(planId);
    try {
      // Use recurring subscription endpoint (mode='subscription')
      const response = await api.post('/billing/subscribe', {
        plan_key: planId,
        quantity: 1,
        origin: window.location.origin,
      });

      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Checkout URL missing — please try again.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout. Please try again.');
    } finally {
      setLoadingPlan(null);
    }
  };

  const calculateDiscountedPrice = (originalPrice) => {
    if (!appliedPromo) return originalPrice;
    
    if (appliedPromo.discount_type === 'percentage') {
      return originalPrice * (1 - appliedPromo.discount_value / 100);
    } else {
      return Math.max(0, originalPrice - appliedPromo.discount_value);
    }
  };

  const faqs = [
    {
      q: "Is Zazu really free?",
      a: "Yes. The Free plan gives your child 1 profile, 5 activities per day, and the basic ABC, 123, and Shapes subjects at $0 forever — no credit card required. Paid plans unlock unlimited activities, AI-adaptive learning, voice input, and Bible curriculum."
    },
    {
      q: "What is the difference between the Christian Education subscription and the Premium plan?",
      a: "Bible curriculum is included in the Premium plan ($9.99/mo) or available as a standalone Christian Education subscription ($2.99/mo, 7-day free trial) for families who want Bible content without the full Premium feature set. Premium also includes AI adaptive learning across every subject, multiplayer games, and unlimited child profiles."
    },
    {
      q: "Does Zazu work on iPad and mobile browsers?",
      a: "Yes. Zazu runs in any modern browser — Safari on iPad, Chrome on Android, and desktop. You can also install it as a PWA (Progressive Web App) from the browser menu for an app-like experience on iOS and Android."
    },
    {
      q: "Who founded Zazu and why?",
      a: "Zazu was built by a small team of homeschool parents and educators who wanted a phonics, math, and Bible curriculum in one place, without the ads and in-app purchases common in kids' apps. Founder page with photo and LinkedIn coming to /about."
    },
    {
      q: "Is my child's data safe?",
      a: "Yes. Zazu is fully COPPA-compliant. We never collect personal data from children under 13 without verified parental consent, all data is encrypted in transit and at rest, we never sell data to third parties, and parents can export or delete their child's data at any time."
    },
    {
      q: "Can I cancel anytime?",
      a: "Yes. Cancel from your account settings in one click. No fees, no forms, no phone calls. If you cancel within 30 days of any paid subscription, we'll refund you fully — no questions asked."
    },
    {
      q: "What subjects are included in each plan?",
      a: "Free: ABCs, Numbers, Shapes. Basic ($4.99/mo): All core subjects (Reading, Writing, Math, Phonics, Science). Family ($6.99/mo): Everything in Basic, up to 5 children, weekly reports, offline access. Premium ($9.99/mo): Everything in Family plus Bible curriculum, multiplayer games, and unlimited profiles."
    },
    {
      q: "Do you offer a discount for schools, churches, or homeschool co-ops?",
      a: "Yes. Volume pricing starts at $1.50/student for organizations with 100+ students, and $3.00/student for smaller groups (25 seats → $2.50, 50 seats → $2.00). Bulk enrollment, teacher accounts, Google Classroom sync, and custom curriculum are included. Contact sales from the enterprise card above."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": f.a
      }
    }))
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-teal-50">
      <SEO
        title="Zazu Pricing — Free and Paid Plans for Families and Schools"
        description="Choose from Free, Basic, Family, or Premium plans starting at $0. Bible curriculum, AI adaptive learning, and parent dashboard included. 30-day money-back guarantee."
        path="/pricing"
        schema={faqSchema}
      />
      {/* Announcement Banner - Above Header */}
      <AnnouncementBanner placement="above_header" />
      
      {/* Header */}
      <header className="p-4">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          {isAuthenticated && (
            <Link to="/dashboard">
              <Button variant="outline">Dashboard</Button>
            </Link>
          )}
        </div>
      </header>

      {/* Announcement Banner - Below Header */}
      <AnnouncementBanner placement="below_header" />

      <main className="max-w-6xl mx-auto px-4 py-8">
        {searchParams.get('welcome') === '1' && (
          <div
            className="mb-6 rounded-2xl border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50 p-4 sm:p-5 flex items-start gap-3"
            data-testid="pricing-welcome-banner"
          >
            <Sparkles className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-fredoka text-lg font-bold text-slate-900">Welcome — your organization is registered!</p>
              <p className="text-sm text-slate-600">
                Pick a plan below to enable billing. Any promo code you entered on signup is applied automatically.
              </p>
            </div>
          </div>
        )}

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            Simple, transparent pricing
          </div>
          <h1 className="text-4xl font-bold text-slate-800 mb-4">
            Choose the perfect plan for your family
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Start free and upgrade anytime. All plans include our award-winning curriculum.
          </p>
        </motion.div>

        {/* Promo Code Section */}
        {isAuthenticated && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-md mx-auto mb-8"
          >
            <Card className="border-dashed border-2 border-emerald-200 bg-emerald-50/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Tag className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-700">Have a promo code?</span>
                </div>
                <PromoCodeInput
                  plan={selectedPlan || 'basic'}
                  onApply={setAppliedPromo}
                  onRemove={() => setAppliedPromo(null)}
                  appliedDiscount={appliedPromo}
                />
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {plans.map((plan, idx) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
            >
              <Card className={`h-full relative ${plan.popular ? 'border-2 border-emerald-500 shadow-xl' : 'border-slate-200'}`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </div>
                )}
                <CardHeader className="pb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                    plan.id === 'free' ? 'bg-slate-100' :
                    plan.id === 'parent_basic' ? 'bg-blue-100' :
                    plan.id === 'parent_family' ? 'bg-emerald-100' : 'bg-amber-100'
                  }`}>
                    <plan.icon className={`w-6 h-6 ${
                      plan.id === 'free' ? 'text-slate-600' :
                      plan.id === 'parent_basic' ? 'text-blue-600' :
                      plan.id === 'parent_family' ? 'text-emerald-600' : 'text-amber-600'
                    }`} />
                  </div>
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <CardDescription className="text-sm">{plan.description}</CardDescription>
                  <div className="mt-4">
                    {appliedPromo && plan.price > 0 ? (
                      <>
                        <span className="text-xl text-slate-400 line-through mr-2">
                          ${plan.price}
                        </span>
                        <span className="text-3xl font-bold text-emerald-600">
                          ${calculateDiscountedPrice(plan.price).toFixed(2)}
                        </span>
                        <span className="text-slate-500 text-sm">/{plan.period}</span>
                        <div className="mt-1">
                          <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                            <Tag className="w-3 h-3" />
                            {appliedPromo.code} applied
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="text-3xl font-bold text-slate-800">
                          {plan.price === 0 ? 'Free' : `$${plan.price}`}
                        </span>
                        {plan.price > 0 && <span className="text-slate-500 text-sm">/{plan.period}</span>}
                      </>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Features Included */}
                  <div className="space-y-2 mb-6">
                    {plan.features.map((feature, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-600">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Features Not Included */}
                  {plan.notIncluded.length > 0 && (
                    <div className="space-y-1.5 mb-6 pt-4 border-t border-slate-100">
                      {plan.notIncluded.slice(0, 3).map((feature, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <X className="w-3 h-3 text-slate-300 flex-shrink-0 mt-1" />
                          <span className="text-xs text-slate-400">{feature}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Subscribe Button */}
                  <Button
                    className={`w-full ${
                      plan.id === 'free' ? '' :
                      plan.id === 'parent_basic' ? 'bg-blue-600 hover:bg-blue-700' :
                      plan.id === 'parent_family' ? 'bg-emerald-600 hover:bg-emerald-700' :
                      'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600'
                    }`}
                    variant={plan.buttonVariant}
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={loadingPlan === plan.id || (user?.subscription_plan === plan.id)}
                    data-testid={`subscribe-${plan.id}-btn`}
                  >
                    {loadingPlan === plan.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : user?.subscription_plan === plan.id ? (
                      'Current Plan'
                    ) : (
                      plan.buttonText
                    )}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Bible curriculum bundling clarification (audit D4.3, FIT) */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
          data-testid="bible-bundling-callout"
        >
          <Card className="border-emerald-200 bg-emerald-50/60">
            <CardContent className="p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <HelpCircle className="w-5 h-5 text-emerald-700" />
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">
                <strong>Bible curriculum bundling:</strong> Bible curriculum is included in the Premium plan ($9.99/mo) or available as a standalone{' '}
                <Link to="/christian" className="text-emerald-700 underline font-medium">Christian Education subscription</Link>{' '}
                ($2.99/mo with a 7-day free trial) for families who want Bible content without the full Premium feature set.
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Money-back guarantee + payment trust marks (audit D3.4) */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-10 flex flex-col md:flex-row items-center justify-center gap-4 text-center"
          data-testid="pricing-trust-marks"
        >
          <div className="inline-flex items-center gap-2 bg-white border border-emerald-200 rounded-full px-4 py-2 text-sm text-slate-700 shadow-sm">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            <span><strong>30-day money-back guarantee</strong> — full refund, no questions asked.</span>
          </div>
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 text-xs text-slate-600 shadow-sm">
            <span className="font-semibold text-slate-800">Secure checkout by</span>
            <span className="px-2 py-0.5 rounded bg-[#635BFF] text-white font-bold text-[10px] tracking-wide">STRIPE</span>
            <span className="px-2 py-0.5 rounded bg-black text-white font-bold text-[10px] tracking-wide"> Pay</span>
          </div>
        </motion.div>

        {/* Custom Enterprise Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-12"
        >
          <Card className="border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Building className="w-7 h-7 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-800">Custom / Enterprise</h3>
                    <p className="text-slate-600">For schools, churches, and organizations with 10+ students</p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <div className="text-center sm:text-right">
                    <p className="text-sm text-purple-600 font-medium">Volume pricing from</p>
                    <p className="text-3xl font-bold text-purple-700">$1.50<span className="text-lg">/student</span></p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Link to="/register/organization">
                      <Button size="lg" className="bg-purple-600 hover:bg-purple-700 w-full" data-testid="subscribe-custom-btn">
                        Start organization signup
                      </Button>
                    </Link>
                    <a
                      href="mailto:sales@zazued.com?subject=Zazu%20Enterprise%20Inquiry&body=Hi%20Zazu%20team%2C%0A%0AWe%27re%20interested%20in%20volume%20pricing.%0A%0AOrganization%20name%3A%0ANumber%20of%20students%3A%0ATimeline%3A%0A%0AThanks%21"
                      className="text-xs text-purple-700 hover:text-purple-900 underline text-center"
                      data-testid="contact-sales-mailto"
                    >
                      Or email sales@zazued.com
                    </a>
                  </div>
                </div>
              </div>
              <div className="grid sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-purple-200">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-purple-500" />
                  <span className="text-sm text-slate-600">Bulk enrollment</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-purple-500" />
                  <span className="text-sm text-slate-600">Teacher accounts</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-purple-500" />
                  <span className="text-sm text-slate-600">Custom curriculum</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-purple-500" />
                  <span className="text-sm text-slate-600">Dedicated support</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Feature Comparison Table */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-center text-slate-800 mb-6">Compare Plans</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-white rounded-xl shadow-lg overflow-hidden">
              <thead>
                <tr className="bg-slate-50">
                  <th className="px-4 py-4 text-left text-sm font-semibold text-slate-600">Feature</th>
                  <th className="px-4 py-4 text-center text-sm font-semibold text-slate-600">Free</th>
                  <th className="px-4 py-4 text-center text-sm font-semibold text-blue-600">Basic<br/><span className="text-xs font-normal">$4.99/mo</span></th>
                  <th className="px-4 py-4 text-center text-sm font-semibold text-emerald-600 bg-emerald-50">Family<br/><span className="text-xs font-normal">$6.99/mo</span></th>
                  <th className="px-4 py-4 text-center text-sm font-semibold text-amber-600">Premium<br/><span className="text-xs font-normal">$9.99/mo</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr>
                  <td className="px-4 py-3 text-sm text-slate-600">Child Profiles</td>
                  <td className="px-4 py-3 text-center text-sm">1</td>
                  <td className="px-4 py-3 text-center text-sm">1</td>
                  <td className="px-4 py-3 text-center text-sm bg-emerald-50/50 font-medium">Up to 5</td>
                  <td className="px-4 py-3 text-center text-sm font-medium text-amber-600">Unlimited</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-slate-600">Daily Activities</td>
                  <td className="px-4 py-3 text-center text-sm">5/day</td>
                  <td className="px-4 py-3 text-center text-sm">Unlimited</td>
                  <td className="px-4 py-3 text-center text-sm bg-emerald-50/50">Unlimited</td>
                  <td className="px-4 py-3 text-center text-sm">Unlimited</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-slate-600">AI Adaptive Learning</td>
                  <td className="px-4 py-3 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="px-4 py-3 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="px-4 py-3 text-center bg-emerald-50/50"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="px-4 py-3 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-slate-600">Voice Input & TTS</td>
                  <td className="px-4 py-3 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="px-4 py-3 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="px-4 py-3 text-center bg-emerald-50/50"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="px-4 py-3 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-slate-600">Weekly Reports</td>
                  <td className="px-4 py-3 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="px-4 py-3 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="px-4 py-3 text-center bg-emerald-50/50"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="px-4 py-3 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-slate-600">Bible Curriculum</td>
                  <td className="px-4 py-3 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="px-4 py-3 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="px-4 py-3 text-center bg-emerald-50/50"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="px-4 py-3 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                </tr>
                <tr style={{display: 'none'}}>
                  <td className="px-4 py-3 text-sm text-slate-600">Languages</td>
                  <td className="px-4 py-3 text-center text-sm">-</td>
                  <td className="px-4 py-3 text-center text-sm">-</td>
                  <td className="px-4 py-3 text-center text-sm bg-emerald-50/50">Spanish</td>
                  <td className="px-4 py-3 text-center text-sm">Spanish, French, Akan</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-slate-600">Multiplayer Games</td>
                  <td className="px-4 py-3 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="px-4 py-3 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="px-4 py-3 text-center bg-emerald-50/50"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="px-4 py-3 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-slate-600">Priority Support</td>
                  <td className="px-4 py-3 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="px-4 py-3 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="px-4 py-3 text-center bg-emerald-50/50"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="px-4 py-3 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm text-slate-600">Ad-Free</td>
                  <td className="px-4 py-3 text-center"><X className="w-4 h-4 text-slate-300 mx-auto" /></td>
                  <td className="px-4 py-3 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="px-4 py-3 text-center bg-emerald-50/50"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                  <td className="px-4 py-3 text-center"><Check className="w-4 h-4 text-emerald-500 mx-auto" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ Section — visible by default per audit D1.4, with FAQPage schema (D5.5) */}
        <div className="max-w-3xl mx-auto" data-testid="pricing-faq-section">
          <div className="flex items-center justify-center gap-2 mb-6">
            <HelpCircle className="w-6 h-6 text-emerald-600" />
            <h2 className="text-2xl font-bold text-slate-800">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <Card key={idx} className="border-slate-200" data-testid={`pricing-faq-${idx}`}>
                <CardContent className="p-5">
                  <h3 className="font-semibold text-slate-800 mb-2">{faq.q}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-slate-600">
            Questions? <Link to="/help" className="text-emerald-600 hover:underline">Contact our support team</Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default PricingPage;
